<template>
  <footer class="legal-footer" role="contentinfo">
    <div class="footer-container">
      <div class="footer-content">
        <div class="footer-section">
          <div class="footer-links">
            <h4>{{ $t('footer.links') }}</h4>
            <nav class="legal-nav" aria-label="Legal navigation">
              <router-link to="/imprint" class="footer-link">
                {{ $t('nav.imprint') }}
              </router-link>
              <router-link to="/privacy" class="footer-link">
                {{ $t('nav.privacy') }}
              </router-link>
              <router-link to="/disclaimer" class="footer-link">
                {{ $t('nav.disclaimer') }}
              </router-link>
              <router-link to="/cookies" class="footer-link">
                {{ $t('cookiePolicy.title') }}
              </router-link>
              <button @click="openCookieSettings" class="footer-link cookie-settings-btn">
                {{ $t('nav.cookieSettings') }}
              </button>
            </nav>
          </div>
        </div>
        
        <div class="footer-section">
          <div class="language-selector">
            <label for="footer-language" class="sr-only">Select Language</label>
            <select 
              id="footer-language"
              v-model="currentLocale" 
              @change="changeLanguage"
              class="language-select"
            >
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
        </div>
      </div>
      
      <div class="footer-bottom">
        <p class="copyright">{{ $t('footer.copyright') }}</p>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: 'LegalFooter',
  data() {
    return {
      currentLocale: this.$i18n.locale
    }
  },
  watch: {
    '$i18n.locale'(newLocale) {
      this.currentLocale = newLocale
    }
  },
  methods: {
    changeLanguage() {
      this.$i18n.locale = this.currentLocale
      
      // Store language preference
      try {
        localStorage.setItem('preferred-language', this.currentLocale)
      } catch {
        // Handle storage errors gracefully
      }
      
      // Update document language
      document.documentElement.lang = this.currentLocale
      
      // Emit event for other components
      this.$emit('language-changed', this.currentLocale)
    },
    
    openCookieSettings() {
      // Find and trigger the cookie consent component
      const cookieConsent = this.$root.$children.find(child => 
        child.$options.name === 'CookieConsent'
      )
      
      if (cookieConsent && cookieConsent.openCustomizer) {
        cookieConsent.openCustomizer()
      } else {
        // Fallback: navigate to cookie settings page
        this.$router.push('/cookie-settings')
      }
    }
  }
}
</script>

<style scoped>
.legal-footer {
  background: #2c3e50;
  color: #ecf0f1;
  margin-top: auto;
  border-top: 1px solid #34495e;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 1rem 1rem;
}

.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 2rem;
  margin-bottom: 1.5rem;
}

.footer-section {
  flex: 1;
}

.footer-links h4 {
  color: #1945FE;
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
}

.legal-nav {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.footer-link {
  color: #bdc3c7;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s ease;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.footer-link:hover,
.footer-link:focus {
  color: #02F0F2;
  text-decoration: underline;
}

.cookie-settings-btn {
  margin-top: 0.25rem;
  border-top: 1px solid #34495e;
  padding-top: 0.75rem;
}

.language-selector {
  display: flex;
  justify-content: flex-end;
}

.language-select {
  background: #34495e;
  color: #ecf0f1;
  border: 1px solid #4a5f7a;
  border-radius: 4px;
  padding: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
}

.language-select:focus {
  outline: 2px solid #02F0F2;
  outline-offset: 2px;
}

.language-select option {
  background: #34495e;
  color: #ecf0f1;
}

.footer-bottom {
  border-top: 1px solid #34495e;
  padding-top: 1rem;
  text-align: center;
}

.copyright {
  margin: 0;
  font-size: 0.8rem;
  color: #95a5a6;
}

.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .footer-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }
  
  .legal-nav {
    align-items: center;
  }
  
  .language-selector {
    justify-content: center;
  }
  
  .footer-container {
    padding: 1.5rem 1rem 1rem 1rem;
  }
}

@media (max-width: 480px) {
  .legal-nav {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .footer-link {
    padding: 0.25rem 0;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .legal-footer {
    background: #000;
    color: #fff;
    border-top-color: #fff;
  }
  
  .footer-link {
    color: #fff;
  }
  
  .footer-link:hover,
  .footer-link:focus {
    color: #ffff00;
  }
  
  .language-select {
    background: #000;
    color: #fff;
    border-color: #fff;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .footer-link {
    transition: none;
  }
}
</style>